import React, { createContext, useContext, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface ThemeContextProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = React.useState<'light' | 'dark'>('light');

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const value = {
    theme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Animation variants for page transitions
export const pageTransitionVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1.0], // cubic-bezier easing
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1.0],
    },
  },
};

interface PageTransitionProps {
  children: ReactNode;
  location?: string;
}

export const PageTransition: React.FC<PageTransitionProps> = ({ 
  children, 
  location 
}) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location || 'page'}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageTransitionVariants}
        className="page-wrapper"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Common layout components with animations
export const FadeIn: React.FC<{ children: ReactNode; delay?: number }> = ({ 
  children, 
  delay = 0 
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5, delay }}
  >
    {children}
  </motion.div>
);

export const SlideIn: React.FC<{ 
  children: ReactNode; 
  direction?: 'left' | 'right' | 'up' | 'down';
  delay?: number;
}> = ({ 
  children, 
  direction = 'up', 
  delay = 0 
}) => {
  const directionMap = {
    left: { x: -30, y: 0 },
    right: { x: 30, y: 0 },
    up: { x: 0, y: 30 },
    down: { x: 0, y: -30 },
  };

  const { x, y } = directionMap[direction];

  return (
    <motion.div
      initial={{ opacity: 0, x, y }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay,
        ease: [0.25, 0.1, 0.25, 1.0], 
      }}
    >
      {children}
    </motion.div>
  );
};

export const StaggeredContainer: React.FC<{
  children: ReactNode;
  staggerChildren?: number;
  delayChildren?: number;
}> = ({ 
  children, 
  staggerChildren = 0.1, 
  delayChildren = 0 
}) => (
  <motion.div
    initial="hidden"
    animate="visible"
    variants={{
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren,
          delayChildren,
        },
      },
    }}
  >
    {children}
  </motion.div>
);

export const StaggeredItem: React.FC<{
  children: ReactNode;
}> = ({ children }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.4,
          ease: [0.25, 0.1, 0.25, 1.0],
        },
      },
    }}
  >
    {children}
  </motion.div>
); 